import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, buildApiUrl } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";

type SupportedSection = {
  key: string;
  label: string;
  description: string;
  supported: boolean;
};

type ReportRun = {
  id: string;
  month: string;
  periodLabel: string;
  status: string;
  generatedAt: string | null;
  sentAt: string | null;
  createdAt: string;
  errorMessage: string | null;
  archived: boolean;
};

type ReportConfig = {
  id: string;
  reportType: string;
  canonicalTemplate: string;
  title: string;
  description: string;
  sections: string[];
  recipients: string[];
  brandingConfig: {
    logoR2Key?: string;
    primaryColor?: string;
    companyName?: string;
  };
  isEnabled: boolean;
  schedule: {
    cronExpression: string;
    nextRunAt: string | null;
    lastRunAt: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
  lastGeneratedAt: string | null;
  runs: ReportRun[];
  unsupportedCapabilities: string[];
};

type TemplateResponse = {
  reportTypes: Array<{ key: string; label: string; description: string; defaultSections: string[] }>;
  sections: SupportedSection[];
  unsupportedCapabilities: string[];
};

type GenerateResponse = {
  report: ReportConfig;
  status: "completed" | "failed" | "skipped";
  runId?: string;
  month: string;
  periodLabel: string;
  sectionsIncluded?: number;
  emailSent?: boolean;
  deliveryMode?: "stored" | "inline";
  reportUrl?: string | null;
  html?: string | null;
  archived?: boolean;
  reason?: string;
  error?: string;
  unsupportedCapabilities: string[];
};

type DraftState = {
  sections: string[];
  recipientsText: string;
  companyName: string;
  primaryColor: string;
  logoR2Key: string;
  isEnabled: boolean;
  scheduleCronExpression: string;
};

const DEFAULT_PRIMARY_COLOR = "#1040CC";
const DEFAULT_MONTH = (() => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
})();

function toDraft(report: ReportConfig | null): DraftState {
  return {
    sections: report?.sections ?? [],
    recipientsText: report?.recipients.join(", ") ?? "",
    companyName: report?.brandingConfig.companyName ?? "",
    primaryColor: report?.brandingConfig.primaryColor ?? DEFAULT_PRIMARY_COLOR,
    logoR2Key: report?.brandingConfig.logoR2Key ?? "",
    isEnabled: report?.isEnabled ?? true,
    scheduleCronExpression: report?.schedule?.cronExpression ?? "0 9 1 * *",
  };
}

function normalizeRecipients(input: string): string[] {
  return input
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
}

export default function ReportBuilderPage() {
  const [reports, setReports] = useState<ReportConfig[]>([]);
  const [templates, setTemplates] = useState<TemplateResponse | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState>(toDraft(null));
  const [month, setMonth] = useState(DEFAULT_MONTH);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewMeta, setPreviewMeta] = useState<{ periodLabel: string; deliveryMode: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [clearingSchedule, setClearingSchedule] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // P2-S1: track blob URLs for cleanup on unmount
  const blobUrlsRef = useRef<string[]>([]);
  useEffect(() => {
    return () => {
      for (const url of blobUrlsRef.current) {
        URL.revokeObjectURL(url);
      }
      blobUrlsRef.current = [];
    };
  }, []);

  const selectedReport = useMemo(
    () => reports.find((report) => report.id === selectedId) ?? null,
    [reports, selectedId],
  );

  const supportedSections = templates?.sections.filter((section) => section.supported) ?? [];
  const unsupportedSections = templates?.sections.filter((section) => !section.supported) ?? [];

  async function loadData() {
    setLoading(true);
    setError(null);

    try {
      const [reportsResponse, templatesResponse] = await Promise.all([
        apiFetch<{ data: ReportConfig[] }>("/crm/reports"),
        apiFetch<{ data: TemplateResponse }>("/crm/reports/templates"),
      ]);

      const nextReports = reportsResponse.data ?? [];
      setReports(nextReports);
      setTemplates(templatesResponse.data ?? null);
      setSelectedId(nextReports[0]?.id ?? null);
      setDraft(toDraft(nextReports[0] ?? null));
    } catch {
      setError("Failed to load the reports workspace.");
      setReports([]);
      setTemplates(null);
      setSelectedId(null);
      setDraft(toDraft(null));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    setDraft(toDraft(selectedReport));
  }, [selectedReport]);

  function updateDraft<K extends keyof DraftState>(key: K, value: DraftState[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function toggleSection(sectionKey: string) {
    setDraft((current) => ({
      ...current,
      sections: current.sections.includes(sectionKey)
        ? current.sections.filter((value) => value !== sectionKey)
        : [...current.sections, sectionKey],
    }));
  }

  async function handleCreate(reportType: string = "revenue_summary") {
    setCreating(true);
    setError(null);
    setNotice(null);

    try {
      const template = templates?.reportTypes.find((entry) => entry.key === reportType);
      const response = await apiFetch<{ data: ReportConfig }>("/crm/reports", {
        method: "POST",
        body: JSON.stringify({
          reportType,
          sections: template?.defaultSections ?? supportedSections.map((section) => section.key),
          recipients: [],
          brandingConfig: { primaryColor: DEFAULT_PRIMARY_COLOR },
          isEnabled: true,
        }),
      });

      const created = response.data;
      // P1-I2: merge into existing list rather than replacing it
      setReports((prev) => [...prev.filter((r) => r.id !== created.id), created]);
      setSelectedId(created.id);
      setDraft(toDraft(created));
      setNotice("Monthly report created.");
    } catch {
      setError("Failed to create the report template.");
    } finally {
      setCreating(false);
    }
  }

  async function handleExportPdf() {
    if (!selectedReport) {
      return;
    }

    setExportingPdf(true);
    setError(null);
    setNotice(null);

    try {
      const response = await fetch(buildApiUrl(`/crm/reports/${selectedReport.id}/export`), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ month, format: "pdf" }),
      });

      if (!response.ok) {
        throw new Error("export_failed");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      blobUrlsRef.current.push(url);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedReport.canonicalTemplate}-${month}.pdf`;
      link.click();
      setNotice(`Exported ${selectedReport.title} as PDF.`);
    } catch {
      setError("Failed to export the report as PDF.");
    } finally {
      setExportingPdf(false);
    }
  }

  async function handleSave() {
    if (!selectedReport) {
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      const response = await apiFetch<{ data: ReportConfig }>(`/crm/reports/${selectedReport.id}`, {
        method: "PUT",
        body: JSON.stringify({
          sections: draft.sections,
          recipients: normalizeRecipients(draft.recipientsText),
          brandingConfig: {
            companyName: draft.companyName || undefined,
            primaryColor: draft.primaryColor || undefined,
            logoR2Key: draft.logoR2Key || undefined,
          },
          isEnabled: draft.isEnabled,
        }),
      });

      const updated = response.data;
      setReports((current) => current.map((report) => (report.id === updated.id ? updated : report)));
      setNotice("Report settings saved.");
    } catch {
      setError("Failed to save report settings.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selectedReport) {
      return;
    }

    setDeleting(true);
    setError(null);
    setNotice(null);

    try {
      await apiFetch(`/crm/reports/${selectedReport.id}`, { method: "DELETE" });
      const remaining = reports.filter((report) => report.id !== selectedReport.id);
      setReports(remaining);
      setSelectedId(remaining[0]?.id ?? null);
      setDraft(toDraft(remaining[0] ?? null));
      setPreviewHtml(null);
      setPreviewMeta(null);
      setNotice("Report removed.");
    } catch {
      setError("Failed to delete the report.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleScheduleSave() {
    if (!selectedReport) {
      return;
    }

    setSavingSchedule(true);
    setError(null);
    setNotice(null);

    try {
      const response = await apiFetch<{ data: ReportConfig }>(`/crm/reports/${selectedReport.id}/schedule`, {
        method: "PATCH",
        body: JSON.stringify({
          cronExpression: draft.scheduleCronExpression,
        }),
      });

      const updated = response.data;
      setReports((current) => current.map((report) => (report.id === updated.id ? updated : report)));
      setDraft(toDraft(updated));
      setNotice("Scheduled delivery saved.");
    } catch {
      setError("Failed to save scheduled delivery.");
    } finally {
      setSavingSchedule(false);
    }
  }

  async function handleScheduleClear() {
    if (!selectedReport) {
      return;
    }

    setClearingSchedule(true);
    setError(null);
    setNotice(null);

    try {
      const response = await apiFetch<{ data: ReportConfig }>(`/crm/reports/${selectedReport.id}/schedule`, {
        method: "DELETE",
      });

      const updated = response.data;
      setReports((current) => current.map((report) => (report.id === updated.id ? updated : report)));
      setDraft(toDraft(updated));
      setNotice("Scheduled delivery cleared.");
    } catch {
      setError("Failed to clear scheduled delivery.");
    } finally {
      setClearingSchedule(false);
    }
  }

  async function handleGenerate() {
    if (!selectedReport) {
      return;
    }

    setGenerating(true);
    setError(null);
    setNotice(null);

    try {
      const response = await apiFetch<{ data: GenerateResponse }>(`/crm/reports/${selectedReport.id}/generate`, {
        method: "POST",
        body: JSON.stringify({ month }),
      });

      const generated = response.data;
      setReports((current) => current.map((report) => (report.id === generated.report.id ? generated.report : report)));
      setPreviewHtml(generated.html ?? null);
      setPreviewMeta(
        generated.html
          ? {
              periodLabel: generated.periodLabel,
              deliveryMode: generated.deliveryMode ?? "inline",
            }
          : null,
      );

      if (generated.status === "completed") {
        setNotice(
          generated.html
            ? `Generated ${generated.periodLabel} as an inline HTML preview.`
            : `Generated ${generated.periodLabel}.`,
        );
      } else if (generated.status === "skipped") {
        setNotice(`Generation skipped: ${generated.reason ?? "already exists"}.`);
      } else {
        setError(generated.error ?? "Report generation failed.");
      }
    } catch {
      setError("Failed to generate the report.");
    } finally {
      setGenerating(false);
    }
  }

  const openPreviewInNewTab = useCallback(() => {
    if (!previewHtml) {
      return;
    }

    const blob = new Blob([previewHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    blobUrlsRef.current.push(url);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => {
      URL.revokeObjectURL(url);
      blobUrlsRef.current = blobUrlsRef.current.filter((u) => u !== url);
    }, 60_000);
  }, [previewHtml]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-border bg-[linear-gradient(135deg,rgba(16,64,204,0.08),rgba(15,23,42,0.02))] p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">Mounted real reports</p>
            <h1 className="text-3xl font-semibold text-foreground">Report templates and exports</h1>
            <p className="max-w-3xl text-sm text-muted-foreground">
              This page now reflects the real mounted scope: five built-in report templates, real data-backed sections, live HTML generation, mounted scheduled email delivery, and PDF export on the same backend seam.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
            <a
              href="/analytics/reports/sources"
              className="rounded-2xl border border-border bg-background/80 px-4 py-3 text-sm font-medium text-foreground transition hover:border-primary/40"
            >
              View safe report sources
            </a>
            {selectedReport ? (
              <div className="rounded-2xl border border-border bg-background/70 px-4 py-3 text-sm text-muted-foreground">
                <p>Last generated</p>
                <p className="font-medium text-foreground">
                  {selectedReport.lastGeneratedAt ? formatDateTime(selectedReport.lastGeneratedAt) : "Not generated yet"}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}
      {notice ? <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div> : null}

      <section className="rounded-3xl border border-border bg-card p-6">
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-foreground">Built-in templates</h2>
          <p className="text-sm text-muted-foreground">Create or reopen the mounted report templates from the same reports seam.</p>
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {(templates?.reportTypes ?? []).map((template) => {
            const existing = reports.find((report) => report.canonicalTemplate === template.key);
            const selected = selectedReport?.canonicalTemplate === template.key;
            return (
              <div key={template.key} className={`rounded-2xl border p-4 ${selected ? "border-primary bg-primary/5" : "border-border bg-background"}`}>
                <p className="font-medium text-foreground">{template.label}</p>
                <p className="mt-1 text-sm text-muted-foreground">{template.description}</p>
                <div className="mt-4 flex gap-2">
                  {existing ? (
                    <button
                      type="button"
                      onClick={() => setSelectedId(existing.id)}
                      className="rounded-full border border-border px-3 py-1.5 text-sm font-medium text-foreground"
                    >
                      Open
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleCreate(template.key)}
                      disabled={creating}
                      className="rounded-full bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-60"
                    >
                      {creating ? "Creating..." : "Create"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {!selectedReport ? (
        <section className="rounded-3xl border border-dashed border-border bg-card p-8 text-center">
          <h2 className="text-xl font-semibold text-foreground">No report template configured yet</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Start with one of the mounted templates above. Each report can be configured independently and exported as HTML preview or PDF.
          </p>
        </section>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <section className="space-y-6 rounded-3xl border border-border bg-card p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">{selectedReport.title}</h2>
                <p className="text-sm text-muted-foreground">{selectedReport.description}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving}
                  className="rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save settings"}
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete()}
                  disabled={deleting}
                  className="rounded-full border border-red-200 px-4 py-2 text-sm font-medium text-red-700 disabled:opacity-60"
                >
                  {deleting ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-3">
                <label className="block text-sm font-medium text-foreground" htmlFor="report-company-name">Company name</label>
                <input
                  id="report-company-name"
                  value={draft.companyName}
                  onChange={(event) => updateDraft("companyName", event.target.value)}
                  className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
                  placeholder="Optional brand label"
                />
              </div>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-foreground" htmlFor="report-primary-color">Primary color</label>
                <input
                  id="report-primary-color"
                  value={draft.primaryColor}
                  onChange={(event) => updateDraft("primaryColor", event.target.value)}
                  className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
                  placeholder={DEFAULT_PRIMARY_COLOR}
                />
              </div>
              <div className="space-y-3 lg:col-span-2">
                <label className="block text-sm font-medium text-foreground" htmlFor="report-logo-key">Logo R2 key</label>
                <input
                  id="report-logo-key"
                  value={draft.logoR2Key}
                  onChange={(event) => updateDraft("logoR2Key", event.target.value)}
                  className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
                  placeholder="Optional existing media key"
                />
              </div>
              <div className="space-y-3 lg:col-span-2">
                <label className="block text-sm font-medium text-foreground" htmlFor="report-recipients">Recipient emails</label>
                <textarea
                  id="report-recipients"
                  value={draft.recipientsText}
                  onChange={(event) => updateDraft("recipientsText", event.target.value)}
                  className="min-h-28 w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
                  placeholder="Comma-separated emails used for scheduled delivery."
                />
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-background/60 p-4">
              <label className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">Module enabled</p>
                  <p className="text-xs text-muted-foreground">Keep this report template available for generation while you edit its contents.</p>
                </div>
                <input
                  type="checkbox"
                  checked={draft.isEnabled}
                  onChange={(event) => updateDraft("isEnabled", event.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
              </label>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Supported sections</h3>
                <p className="text-sm text-muted-foreground">Only sections backed by mounted data sources can be selected.</p>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {supportedSections.map((section) => {
                  const checked = draft.sections.includes(section.key);
                  return (
                    <label
                      key={section.key}
                      className={`rounded-2xl border px-4 py-4 transition ${checked ? "border-primary bg-primary/5" : "border-border bg-background"}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSection(section.key)}
                          className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        />
                        <div>
                          <p className="font-medium text-foreground">{section.label}</p>
                          <p className="mt-1 text-sm text-muted-foreground">{section.description}</p>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-medium text-amber-900">Explicitly out of scope</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {unsupportedSections.map((section) => (
                  <span key={section.key} className="rounded-full border border-amber-300 px-3 py-1 text-xs font-medium text-amber-900">
                    {section.label}
                  </span>
                ))}
                {(templates?.unsupportedCapabilities ?? []).map((capability) => (
                  <span key={capability} className="rounded-full border border-amber-300 px-3 py-1 text-xs font-medium text-amber-900">
                    {capability}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-background/60 p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-foreground">Scheduled delivery</h3>
                  <p className="text-sm text-muted-foreground">
                    The mounted cron seam sends the previous month&apos;s report when this cron becomes due. Times are evaluated in UTC.
                  </p>
                </div>
                <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                  <div>
                    <p className="font-medium text-foreground">Next run</p>
                    <p>{selectedReport.schedule?.nextRunAt ? formatDateTime(selectedReport.schedule.nextRunAt) : "Not scheduled"}</p>
                  </div>
                  <div>
                    <p className="font-medium text-foreground">Last sent</p>
                    <p>{selectedReport.schedule?.lastRunAt ? formatDateTime(selectedReport.schedule.lastRunAt) : "Not sent yet"}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_auto_auto]">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-foreground" htmlFor="report-schedule-cron">Cron expression</label>
                  <input
                    id="report-schedule-cron"
                    value={draft.scheduleCronExpression}
                    onChange={(event) => updateDraft("scheduleCronExpression", event.target.value)}
                    className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
                    placeholder="0 9 1 * *"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void handleScheduleSave()}
                  disabled={savingSchedule}
                  className="rounded-full border border-border px-4 py-3 text-sm font-medium text-foreground disabled:opacity-60"
                >
                  {savingSchedule ? "Saving..." : "Save schedule"}
                </button>
                <button
                  type="button"
                  onClick={() => void handleScheduleClear()}
                  disabled={clearingSchedule}
                  className="rounded-full border border-border px-4 py-3 text-sm font-medium text-foreground disabled:opacity-60"
                >
                  {clearingSchedule ? "Clearing..." : "Clear schedule"}
                </button>
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <div className="rounded-3xl border border-border bg-card p-6">
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Generate report</h2>
                  <p className="text-sm text-muted-foreground">
                    Generation uses the mounted report engine and returns an inline HTML preview. Scheduled sending and PDF export are both mounted on the same reports seam.
                  </p>
                </div>
                <label className="space-y-2">
                  <span className="text-sm font-medium text-foreground">Report month</span>
                  <input
                    type="month"
                    value={month}
                    onChange={(event) => setMonth(event.target.value)}
                    className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => void handleGenerate()}
                  disabled={generating || draft.sections.length === 0}
                  className="rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground disabled:opacity-60"
                >
                  {generating ? "Generating..." : "Generate HTML report"}
                </button>
                <button
                  type="button"
                  onClick={() => void handleExportPdf()}
                  disabled={exportingPdf || draft.sections.length === 0}
                  className="rounded-full border border-border px-5 py-3 text-sm font-medium text-foreground disabled:opacity-60"
                >
                  {exportingPdf ? "Exporting..." : "Export PDF"}
                </button>
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-card p-6">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-foreground">Recent runs</h2>
                  <p className="text-sm text-muted-foreground">These rows come from the real `report_runs` table.</p>
                </div>
                {previewHtml ? (
                  <button
                    type="button"
                    onClick={openPreviewInNewTab}
                    className="rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground"
                  >
                    Open preview
                  </button>
                ) : null}
              </div>

              {selectedReport.runs.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">No report runs yet.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {selectedReport.runs.map((run) => (
                    <div key={run.id} className="rounded-2xl border border-border bg-background px-4 py-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-foreground">{run.periodLabel}</p>
                          <p className="text-xs text-muted-foreground">
                            Created {formatDateTime(run.createdAt)}
                            {run.generatedAt ? ` · Generated ${formatDateTime(run.generatedAt)}` : ""}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium capitalize text-foreground">{run.status}</p>
                          <p className="text-xs text-muted-foreground">{run.archived ? "Archived delivery mounted" : "Inline-only mounted run"}</p>
                        </div>
                      </div>
                      {run.errorMessage ? <p className="mt-3 text-sm text-red-700">{run.errorMessage}</p> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {previewHtml ? (
              <div className="rounded-3xl border border-border bg-card p-6">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Latest HTML preview</h2>
                    <p className="text-sm text-muted-foreground">
                      {previewMeta ? `${previewMeta.periodLabel} · ${previewMeta.deliveryMode}` : "Inline preview"}
                    </p>
                  </div>
                </div>
                <iframe
                  title="Report preview"
                  srcDoc={previewHtml}
                  className="min-h-[520px] w-full rounded-2xl border border-border bg-white"
                />
              </div>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}
